import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, Container, Img, ParallaxImg } from "@/components/common/Primitives";
import { PageHero, SectionHead, Kicker, Headline, Lead, RuleDraw, ClosingBand, Index } from "@/components/common/Editorial";
import { PHONE } from "@/components/layout/Header";
import { useLang } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";

/* copy (binding – memory/copy_deck_nynorsk.md §7) */
const PLACE_IMAGES = {
  Baroniet: { src: "/images/baroniet-roses.jpg", position: "50% 55%" },
  Fjorden: { src: "/images/pier-wide.jpg", position: "50% 60%" },
  Fjella: { src: "/images/peaks-hiker.jpg", position: "50% 50%" },
  Bondhusdalen: { src: "/images/skalafjell.jpg", position: "50% 50%" },
};

export default function About() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  usePageMeta(
    tx("Om hotellet", "About the hotel"),
    tx("Rosendal Fjordhotel ligg ved Hardangerfjorden, med Baroniet i gåavstand og fjellturar i nærleiken. 89 rom, hytter ved sjøen og Restaurant Baronen.", "Rosendal Fjordhotel sits on the Hardangerfjord, with the Barony within walking distance and mountain hikes nearby. 89 rooms, cabins by the sea and Restaurant Baronen."),
  );
  const story = data?.story || {};
  const people = story.people || [];
  const timeline = story.timeline || [];
  const hotel = story.hotel || {};
  const paras = (lang === "en" ? hotel.en : hotel.nn) || [];

  return (
    <div data-testid="about-page">
      <PageHero
        testid="about-hero"
        image="/images/hotel-glass-sun.jpg"
        position="50% 60%"
        alt={tx("Rosendal Fjordhotel med glasdelen i sol og fjella bak", "Rosendal Fjordhotel with the glass pavilion in sunshine and mountains behind")}
        kicker={tx("Om hotellet", "About the hotel")}
        title={tx("Velkomen til Rosendal", "Welcome to Rosendal")}
        lead={tr(story.intro) || tx("Rosendal Fjordhotel ligg ved Hardangerfjorden, med Baroniet i gåavstand og fjellturar i nærleiken. Her kan du bu ei helg, samle familien til selskap eller møtast med kollegaer.", "Rosendal Fjordhotel sits on the Hardangerfjord, with the Barony within walking distance and mountain hikes close by. Stay for a weekend, gather the family for a celebration or meet with colleagues.")}
      />

      {/* The hotel */}
      <section className="pt-24 lg:pt-32" data-testid="about-story">
        <Container className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <Kicker className="mb-5">{tx("Hotellet", "The hotel")}</Kicker>
            <Headline>{tr(hotel.title) || tx("Hotellet ved fjorden", "The hotel by the fjord")}</Headline>
            {paras.map((p, i) => <Lead key={i} className={i > 0 ? "mt-4" : ""}>{p}</Lead>)}
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="about-rooms-cta"><Link to="/rom">{tx("Sjå rom og hytter", "See rooms and cabins")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
              <Button asChild size="lg" variant="outline" data-testid="about-contact-cta"><Link to="/praktisk#kontakt">{tx("Kontakt hotellet", "Contact the hotel")}</Link></Button>
            </div>
            <dl className="mt-12 grid grid-cols-3 gap-6 border-t border-border pt-8" data-testid="about-facts">
              {[[89, tx("rom", "rooms")], [tx("Hytter", "Cabins"), tx("ved sjøen", "by the sea")], ["Baronen", tx("restaurant", "restaurant")]].map(([v, l]) => (
                <div key={String(v)}>
                  <dt className="text-[1.75rem] font-medium leading-none tracking-[-0.02em] text-foreground tnum sm:text-[2rem]">{v}</dt>
                  <dd className="mt-2 text-sm text-muted-foreground">{l}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.05}>
            <div className="grid grid-cols-5 gap-3 sm:gap-4">
              <ParallaxImg src="/images/hotel-exterior.jpg" alt={tx("Hotellet i haustfargar under fjella", "The hotel in autumn colours below the mountains")} className="col-span-3 aspect-[4/5]" sizes={SIZES.half} position="50% 60%" range={4} />
              <div className="col-span-2 flex flex-col gap-3 pt-10 sm:gap-4 sm:pt-14">
                <Img src="/images/restaurant-hall-wide.jpg" alt={tx("Restaurant Baronen", "Restaurant Baronen")} className="aspect-[3/4]" sizes={SIZES.quarter} />
                <Img src="/images/pier.jpg" alt={tx("Brygga ved hotellet", "The pier by the hotel")} className="aspect-square" sizes={SIZES.quarter} />
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Village, fjord and mountains */}
      <section className="pt-24 lg:pt-32" data-testid="about-places">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <Reveal><SectionHead kicker={tx("Rosendal", "Rosendal")} title={tx("Bygda, fjorden og fjella", "The village, the fjord and the mountains")} action={tx("Praktisk informasjon", "Practical information")} to="/praktisk" testid="about-places-title" /></Reveal>
          <ol className="mt-12 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:mt-16" data-testid="about-timeline">
            {timeline.map((t, i) => {
              const img = PLACE_IMAGES[t.year] || PLACE_IMAGES.Fjorden;
              return (
                <Reveal as="li" key={t.year} delay={(i % 2) * 0.05}>
                  <Img src={img.src} alt={t.year} className="img-zoom aspect-[3/2]" position={img.position} sizes={SIZES.half} />
                  <div className="mt-6">
                    <Index n={i + 1} />
                    <h3 className="mt-4 text-[1.5rem] font-medium leading-snug tracking-[-0.015em] text-foreground">{t.year}</h3>
                    <p className="mt-2 max-w-[48ch] text-[1rem] leading-relaxed text-muted-foreground">{lang === "en" ? t.en : t.nn}</p>
                  </div>
                </Reveal>
              );
            })}
          </ol>
        </Container>
      </section>

      {/* People */}
      <section className="pt-24 lg:pt-32" data-testid="about-people">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-4">
              <Kicker className="mb-5">{tx("Baronen", "Baronen")}</Kicker>
              <Headline>{tx("På kjøkenet og i restauranten", "In the kitchen and the restaurant")}</Headline>
            </Reveal>
            <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:col-span-8">
              {people.map((p, i) => (
                <Reveal key={p.name} delay={i * 0.05}>
                  <figure data-testid={`about-person-${i}`}>
                    <Img src={p.image} alt={p.name} className="aspect-[4/5]" imgClassName="object-top" sizes={SIZES.half} />
                    <figcaption className="mt-6 border-t border-border pt-5">
                      <p className="text-[1.25rem] font-medium leading-snug text-foreground">{p.name}</p>
                      <p className="label-caps mt-1.5 text-muted-foreground">{tr(p.role)}</p>
                      {p.text && <p className="mt-4 max-w-[44ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{tr(p.text)}</p>}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <ClosingBand
        testid="about-closing"
        className="mt-24 lg:mt-32"
        title={tx("Finn rom til opphaldet", "Find a room for your stay")}
        lead={tx("Vel datoane dine og sjå ledige rom. Frukost er inkludert i alle hotellrom.", "Choose your dates and see available rooms. Breakfast is included in all hotel rooms.")}
        image="/images/hotel-mountain.jpg"
        position="50% 55%"
      >
        <Button asChild size="lg" variant="inverse" data-testid="about-book-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
        <Button asChild size="lg" variant="outline-light" className="tabular" data-testid="about-closing-phone"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring", "Call")} {PHONE.display}</a></Button>
      </ClosingBand>
    </div>
  );
}
